import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export interface ProfileData {
    id?: string;
    user_id?: string;
    email?: string;
    first_name: string;
    last_name: string;
    phone_number: string;
    job_title: string;
    organization_name: string;
    organization_type: string;
    organization_size: string;
    years_experience: string;
    location: string;
    how_did_you_hear: string;
    role: string;
    tech_comfort: string;
    ai_experience: string;
    learning_style: string;
    primary_channel?: string;
    key_audience?: string;
    reminder_style?: string;
    tone_glossary?: string[];
    ai_insights?: {
        narrative_bio?: string;
        strengths?: string[];
        communication_style?: string;
        recommended_focus?: string;
    };
    current_goal?: string;
    onboarding_step?: number | null;
    profile_completed: boolean;
}

export const useProfile = () => {
    const { user } = useAuth();
    const [profile, setProfile] = useState<ProfileData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchProfile = useCallback(async () => {
        if (!user) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('user_id', user.id)
                .single();

            if (error && error.code !== 'PGRST116') {
                throw error;
            }

            if (data) {
                const parsedTone =
                    Array.isArray(data.tone_glossary)
                        ? (data.tone_glossary.filter(Boolean) as string[])
                        : typeof data.tone_glossary === 'object' && data.tone_glossary !== null
                            ? (Object.values(data.tone_glossary as Record<string, string>).filter(Boolean) as string[])
                            : typeof data.tone_glossary === 'string' && data.tone_glossary.length > 0
                                ? [data.tone_glossary]
                                : [];

                setProfile({
                    id: data.id || '',
                    user_id: data.user_id || '',
                    email: data.email || '',
                    first_name: data.first_name || '',
                    last_name: data.last_name || '',
                    phone_number: data.phone_number || '',
                    job_title: data.job_title || '',
                    organization_name: data.organization_name || '',
                    organization_type: data.organization_type || '',
                    organization_size: data.organization_size || '',
                    years_experience: data.years_experience || '',
                    location: data.location || '',
                    how_did_you_hear: data.how_did_you_hear || '',
                    role: data.role || '',
                    tech_comfort: data.tech_comfort || '',
                    ai_experience: data.ai_experience || '',
                    learning_style: data.learning_style || '',
                    primary_channel: data.primary_channel || '',
                    key_audience: data.key_audience || '',
                    reminder_style: data.reminder_style || '',
                    tone_glossary: parsedTone,
                    ai_insights: (data as any).ai_insights || {},
                    current_goal: (data as any).current_goal || '',
                    onboarding_step: data.onboarding_step ?? null,
                    profile_completed: data.profile_completed || false
                });
            } else {
                // Initialize empty profile if none exists
                setProfile({
                    id: '',
                    user_id: '',
                    email: '',
                    first_name: '',
                    last_name: '',
                    phone_number: '',
                    job_title: '',
                    organization_name: '',
                    organization_type: '',
                    organization_size: '',
                    years_experience: '',
                    location: '',
                    how_did_you_hear: '',
                    role: '',
                    tech_comfort: '',
                    ai_experience: '',
                    learning_style: '',
                    primary_channel: '',
                    key_audience: '',
                    reminder_style: '',
                    tone_glossary: [],
                    current_goal: '',
                    onboarding_step: null,
                    profile_completed: false
                });
            }
        } catch (err: any) {
            console.error('Error fetching profile:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    return { profile, loading, error, refetch: fetchProfile, setProfile };
};
